import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { 
  Trophy, CheckCircle2, XCircle, ArrowRight, RotateCcw, 
  Scale, Dumbbell, Heart, TrendingUp, TrendingDown, Target,
  Loader2, Sparkles, ChevronRight, BarChart3, Flame, Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MesocycleEndScreen = ({ onClose, onAdvance }) => {
  const [status, setStatus] = useState(null);
  const [testQuestions, setTestQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState('summary'); // 'summary', 'test', 'result'
  const [mesocycleSummary, setMesocycleSummary] = useState(null);

  useEffect(() => {
    fetchStatus();
    fetchMesocycleSummary();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/me/mesocycle-status`);
      setStatus(response.data);
      
      // Si ya aprobó el test, mostrar resultado
      if (response.data.test_passed) {
        setPhase('result');
        setTestResult({ passed: true });
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMesocycleSummary = async () => {
    try {
      const response = await axios.get(`${API}/me/mesocycle-summary`);
      setMesocycleSummary(response.data);
    } catch (error) {
      console.error('Error fetching mesocycle summary:', error);
    }
  };

  const fetchTest = async () => {
    try {
      const response = await axios.get(`${API}/me/mesocycle-test`);
      setTestQuestions(response.data.questions);
      setPhase('test');
    } catch (error) {
      toast.error('Error al cargar el test');
    }
  };

  const submitTest = async () => {
    // Verificar que todas las preguntas estén respondidas
    if (Object.keys(answers).length < testQuestions.length) {
      toast.error('Responde todas las preguntas');
      return;
    }

    setSubmitting(true);
    try {
      const formattedAnswers = Object.entries(answers).map(([qId, answer]) => ({
        question_id: qId,
        answer: answer
      }));

      const response = await axios.post(`${API}/me/mesocycle-test`, {
        answers: formattedAnswers
      });

      setTestResult(response.data);
      setPhase('result');

      if (response.data.passed) {
        toast.success('¡Felicidades! Has aprobado el test 🎉');
      } else {
        toast.error(`Necesitas ${8 - response.data.score} respuestas más`);
      }
    } catch (error) {
      toast.error('Error al enviar el test');
    } finally {
      setSubmitting(false);
    }
  };

  const advanceToNextMesocycle = async () => {
    try {
      await axios.post(`${API}/me/advance-mesocycle`);
      toast.success('¡Bienvenido al nuevo mesociclo! 💪');
      if (onAdvance) onAdvance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al avanzar');
    }
  };

  const retryTest = () => {
    setAnswers({});
    setTestResult(null);
    setPhase('test');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // ============== PHASE: SUMMARY ==============
  if (phase === 'summary') {
    const initialWeight = status?.initial_physio?.weight;
    const currentWeight = status?.current_physio?.weight;
    const weightChange = initialWeight && currentWeight ? (currentWeight - initialWeight).toFixed(1) : null;
    
    const initialFat = status?.initial_physio?.body_fat;
    const currentFat = status?.current_physio?.body_fat;
    const fatChange = initialFat && currentFat ? (currentFat - initialFat).toFixed(1) : null;
    
    const initialMuscle = status?.initial_physio?.muscle_mass;
    const currentMuscle = status?.current_physio?.muscle_mass;
    const muscleChange = initialMuscle && currentMuscle ? (currentMuscle - initialMuscle).toFixed(1) : null;

    return (
      <div className="space-y-6 p-4 max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 rounded-full">
            <Trophy className="h-5 w-5 text-primary" />
            <span className="text-lg font-bold text-primary">¡Mesociclo {status?.current_mesocycle} Completado!</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">{status?.plan_name}</h1>
        </div>

        {/* VINISTE ASÍ */}
        {status?.initial_physio && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-muted-foreground flex items-center gap-2">
                <span className="text-xl">📍</span> VINISTE ASÍ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-foreground">{status.initial_physio.weight || '-'}</p>
                  <p className="text-xs text-muted-foreground">kg</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{status.initial_physio.body_fat || '-'}</p>
                  <p className="text-xs text-muted-foreground">% grasa</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{status.initial_physio.muscle_mass || '-'}</p>
                  <p className="text-xs text-muted-foreground">% músculo</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ESTÁS AQUÍ */}
        {status?.current_physio && (
          <Card className="bg-gradient-to-r from-primary/10 to-amber-500/10 border-primary/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-primary flex items-center gap-2">
                <span className="text-xl">🎯</span> ESTÁS AQUÍ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-foreground">{status.current_physio.weight || '-'}</p>
                  <p className="text-xs text-muted-foreground">kg</p>
                  {weightChange && (
                    <p className={`text-sm font-bold ${parseFloat(weightChange) <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {parseFloat(weightChange) > 0 ? '+' : ''}{weightChange}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{status.current_physio.body_fat || '-'}</p>
                  <p className="text-xs text-muted-foreground">% grasa</p>
                  {fatChange && (
                    <p className={`text-sm font-bold ${parseFloat(fatChange) <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {parseFloat(fatChange) > 0 ? '+' : ''}{fatChange}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{status.current_physio.muscle_mass || '-'}</p>
                  <p className="text-xs text-muted-foreground">% músculo</p>
                  {muscleChange && (
                    <p className={`text-sm font-bold ${parseFloat(muscleChange) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {parseFloat(muscleChange) > 0 ? '+' : ''}{muscleChange}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* HAS CONSEGUIDO */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-amber-400 flex items-center gap-2">
              <span className="text-xl">🏆</span> HAS CONSEGUIDO
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-400" />
              <span className="text-foreground">
                {status?.sessions_completed}/{status?.total_sessions_mesocycle} sesiones completadas
              </span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-blue-500/10 rounded-lg">
              <Target className="h-5 w-5 text-blue-400" />
              <span className="text-foreground">5 semanas de entrenamiento constante</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-purple-500/10 rounded-lg">
              <Sparkles className="h-5 w-5 text-purple-400" />
              <span className="text-foreground">4 píldoras de conocimiento aprendidas</span>
            </div>
          </CardContent>
        </Card>

        {/* INFORME DE FUERZA - DATOS OBJETIVOS */}
        {mesocycleSummary && !mesocycleSummary.error && (
          <Card className="bg-gradient-to-br from-cyan-500/10 to-blue-500/5 border-cyan-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-cyan-400 flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                INFORME DE FUERZA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Resumen General */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-cyan-500/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-cyan-300">{mesocycleSummary.total_volume_tons}</p>
                  <p className="text-xs text-cyan-300/70">Toneladas levantadas</p>
                </div>
                <div className="bg-cyan-500/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-cyan-300">{mesocycleSummary.total_sets}</p>
                  <p className="text-xs text-cyan-300/70">Series totales</p>
                </div>
              </div>

              {/* Análisis de Ejercicios */}
              <div className="bg-black/20 rounded-lg p-3 space-y-2">
                <p className="text-sm font-semibold text-foreground mb-2">Análisis de {mesocycleSummary.exercises_analysis?.total} ejercicios:</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-green-500/20 rounded p-2 text-center">
                    <TrendingUp className="h-4 w-4 text-green-400 mx-auto mb-1" />
                    <p className="text-lg font-bold text-green-400">{mesocycleSummary.exercises_analysis?.improved}</p>
                    <p className="text-[10px] text-green-300/70">Mejoraron</p>
                  </div>
                  <div className="flex-1 bg-yellow-500/20 rounded p-2 text-center">
                    <Target className="h-4 w-4 text-yellow-400 mx-auto mb-1" />
                    <p className="text-lg font-bold text-yellow-400">{mesocycleSummary.exercises_analysis?.maintained}</p>
                    <p className="text-[10px] text-yellow-300/70">Mantuvieron</p>
                  </div>
                  <div className="flex-1 bg-red-500/20 rounded p-2 text-center">
                    <TrendingDown className="h-4 w-4 text-red-400 mx-auto mb-1" />
                    <p className="text-lg font-bold text-red-400">{mesocycleSummary.exercises_analysis?.decreased}</p>
                    <p className="text-[10px] text-red-300/70">Bajaron</p>
                  </div>
                </div>
              </div>

              {/* Top Ejercicios con más Progresión */}
              {mesocycleSummary.top_exercises?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Flame className="h-4 w-4 text-orange-400" />
                    Top Ejercicios con más Progresión:
                  </p>
                  {mesocycleSummary.top_exercises.slice(0, 3).map((ex, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-black/30 rounded-lg p-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</span>
                        <span className="text-sm text-foreground">{ex.name}</span>
                      </div>
                      <div className="text-right">
                        <span className={`text-sm font-bold ${ex.progression > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {ex.progression > 0 ? '+' : ''}{ex.progression}%
                        </span>
                        <p className="text-[10px] text-muted-foreground">
                          {ex.first_load}kg → {ex.last_load}kg
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Conclusiones */}
              {mesocycleSummary.conclusions?.length > 0 && (
                <div className="border-t border-cyan-500/20 pt-3 space-y-1">
                  <p className="text-sm font-semibold text-cyan-400 mb-2">📋 Conclusiones:</p>
                  {mesocycleSummary.conclusions.map((conclusion, idx) => (
                    <p key={idx} className="text-sm text-foreground">{conclusion}</p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Botón para el Test */}
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4">
            <div className="text-center space-y-3">
              <p className="text-foreground font-medium">
                Para pasar al siguiente mesociclo, completa el test de las píldoras
              </p>
              <p className="text-sm text-muted-foreground">
                10 preguntas de Verdadero/Falso • Necesitas 8/10 para aprobar
              </p>
              <Button 
                onClick={fetchTest}
                className="w-full h-12 text-lg font-bold bg-amber-500 hover:bg-amber-600 text-black"
              >
                Comenzar Test
                <ChevronRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============== PHASE: TEST ==============
  if (phase === 'test') {
    const answeredCount = Object.keys(answers).length;
    const progress = (answeredCount / testQuestions.length) * 100;

    return (
      <div className="space-y-6 p-4 max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-foreground">Test de Mesociclo {status?.current_mesocycle}</h1>
          <p className="text-muted-foreground">Verdadero o Falso • {answeredCount}/{testQuestions.length} respondidas</p>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Questions */}
        <div className="space-y-4">
          {testQuestions.map((q, idx) => (
            <Card 
              key={q.id} 
              className={`border transition-all ${
                answers[q.id] !== undefined 
                  ? 'border-primary/50 bg-primary/5' 
                  : 'border-border bg-card'
              }`}
            >
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-full font-bold">
                    {idx + 1}
                  </span>
                  <div className="flex-1">
                    <p className="text-foreground font-medium">{q.question}</p>
                    <p className="text-xs text-muted-foreground mt-1">📚 {q.pildora_title}</p>
                  </div>
                </div>
                <div className="flex gap-3 pl-11">
                  <Button
                    variant={answers[q.id] === true ? "default" : "outline"}
                    onClick={() => setAnswers({...answers, [q.id]: true})}
                    className={`flex-1 ${answers[q.id] === true ? 'bg-green-600 hover:bg-green-700' : ''}`}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Verdadero
                  </Button>
                  <Button
                    variant={answers[q.id] === false ? "default" : "outline"}
                    onClick={() => setAnswers({...answers, [q.id]: false})}
                    className={`flex-1 ${answers[q.id] === false ? 'bg-red-600 hover:bg-red-700' : ''}`}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Falso
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Submit Button */}
        <Button 
          onClick={submitTest}
          disabled={submitting || answeredCount < testQuestions.length}
          className="w-full h-14 text-lg font-bold"
        >
          {submitting ? (
            <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Enviando...</>
          ) : (
            <>Enviar Respuestas ({answeredCount}/{testQuestions.length})</>
          )}
        </Button>
      </div>
    );
  }

  // ============== PHASE: RESULT ==============
  if (phase === 'result') {
    const passed = testResult?.passed || status?.test_passed;

    return (
      <div className="space-y-6 p-4 max-w-2xl mx-auto">
        {/* Result Card */}
        <Card className={`border-2 ${passed ? 'border-green-500 bg-green-500/10' : 'border-red-500 bg-red-500/10'}`}>
          <CardContent className="p-8 text-center space-y-4">
            {passed ? (
              <>
                <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
                  <Trophy className="h-10 w-10 text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-green-400">¡Test Aprobado!</h2>
                {testResult && (
                  <p className="text-lg text-foreground">
                    {testResult.score}/{testResult.total} correctas ({testResult.percentage}%)
                  </p>
                )}
                <p className="text-muted-foreground">
                  Has demostrado que entiendes los fundamentos del entrenamiento.
                  <br />¡Estás listo para el siguiente nivel!
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 mx-auto bg-red-500/20 rounded-full flex items-center justify-center">
                  <XCircle className="h-10 w-10 text-red-400" />
                </div>
                <h2 className="text-2xl font-bold text-red-400">Sigue Intentando</h2>
                <p className="text-lg text-foreground">
                  {testResult?.score}/{testResult?.total} correctas ({testResult?.percentage}%)
                </p>
                <p className="text-muted-foreground">
                  Necesitas 8/10 para aprobar. ¡Repasa las píldoras e inténtalo de nuevo!
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        {passed ? (
          <Button 
            onClick={advanceToNextMesocycle}
            className="w-full h-14 text-lg font-bold bg-green-600 hover:bg-green-700"
          >
            <ArrowRight className="h-5 w-5 mr-2" />
            Comenzar Mesociclo {(status?.current_mesocycle || 1) + 1}
          </Button>
        ) : (
          <div className="space-y-3">
            <Button 
              onClick={retryTest}
              className="w-full h-14 text-lg font-bold"
            >
              <RotateCcw className="h-5 w-5 mr-2" />
              Intentar de Nuevo
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              Puedes repetir el test las veces que necesites
            </p>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default MesocycleEndScreen;
